import { useState, useEffect, useRef } from "react";

interface AutocompleteResult {
  screen_name: string;
  name: string;
  profile_image_url: string;
  followers_count: number;
}

export function useHandleAutocomplete(query: string, enabled: boolean) {
  const [results, setResults] = useState<AutocompleteResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const clean = query.trim().replace(/^@/, "");
    if (!enabled || clean.length < 2) {
      setResults([]);
      return;
    }

    const timeout = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsLoading(true);

      try {
        const resp = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/autocomplete-handle`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            },
            body: JSON.stringify({ query: clean }),
            signal: controller.signal,
          }
        );
        const data = await resp.json();
        if (!controller.signal.aborted) {
          setResults(data.results || []);
        }
      } catch (e) {
        if (!(e instanceof DOMException && e.name === "AbortError")) {
          setResults([]);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }, 300); // 300ms debounce

    return () => {
      clearTimeout(timeout);
      abortRef.current?.abort();
    };
  }, [query, enabled]);

  return { results, isLoading, clear: () => setResults([]) };
}
